import { useEffect, useState } from 'react'
import {
  useAssistantContextStore,
  type AssistantFieldEditorContext,
  type AssistantGridContext,
  type AssistantHeaderContext,
  type AssistantTableContext,
} from '../stores/useAssistantContextStore'

/**
 * Keeps one slot of the assistant's page context in step with what a page
 * shows, and clears it when the page goes away. Values are compared by their
 * JSON, so callers can pass a fresh object every render.
 */
function useRegisterSlot<T>(
  value: T | null,
  set: (owner: symbol, value: T) => void,
  clear: (owner: symbol) => void
): void {
  const [owner] = useState(() => Symbol('assistant-context'))
  const serialized = JSON.stringify(value)

  useEffect(() => {
    const next = JSON.parse(serialized) as T | null
    if (next === null) clear(owner)
    else set(owner, next)
  }, [clear, owner, serialized, set])

  useEffect(() => () => clear(owner), [clear, owner])
}

/** Registers a page header's title, trail, tab and statuses. Used by `PageHeader`. */
export function useRegisterAssistantHeader(value: AssistantHeaderContext | null): void {
  const setHeader = useAssistantContextStore((state) => state.setHeader)
  const clearHeader = useAssistantContextStore((state) => state.clearHeader)
  useRegisterSlot(value, setHeader, clearHeader)
}

/** Registers the table and view the Tables page shows. */
export function useRegisterAssistantTable(value: AssistantTableContext | null): void {
  const setTable = useAssistantContextStore((state) => state.setTable)
  const clearTable = useAssistantContextStore((state) => state.clearTable)
  useRegisterSlot(value, setTable, clearTable)
}

/** Registers what a grid that isn't a data table shows. Used by `TableWorkspace`. */
export function useRegisterAssistantGrid(value: AssistantGridContext | null): void {
  const setGrid = useAssistantContextStore((state) => state.setGrid)
  const clearGrid = useAssistantContextStore((state) => state.clearGrid)
  useRegisterSlot(value, setGrid, clearGrid)
}

/** Registers Field Settings and its draft while it's open. Used by `ColumnManageDialog`. */
export function useRegisterAssistantFieldEditor(value: AssistantFieldEditorContext | null): void {
  const setFieldEditor = useAssistantContextStore((state) => state.setFieldEditor)
  const clearFieldEditor = useAssistantContextStore((state) => state.clearFieldEditor)
  useRegisterSlot(value, setFieldEditor, clearFieldEditor)
}

/**
 * Registers messages the screen shows (a save bar's problems, a warning bar,
 * an error toast) so the assistant can answer about them. Several components
 * register at once; an empty list registers nothing.
 */
export function useRegisterAssistantNotices(lines: readonly string[]): void {
  const setNotices = useAssistantContextStore((state) => state.setNotices)
  const clearNotices = useAssistantContextStore((state) => state.clearNotices)
  useRegisterSlot(lines.length > 0 ? [...lines] : null, setNotices, clearNotices)
}
